// Cyclops Pricing Data - Q1/26
// Complete typed catalog with all products and services

export type PricingCategory =
  | 'pilot_packages'
  | 'licenses'
  | 'maps'
  | 'support'
  | 'integration'
  | 'rnd'
  | 'field_services'

export type SelectionMode = 'single' | 'multiple' | 'quantity'

export interface PricingItem {
  id: string
  partNumber: string
  name: string
  description: string
  price: number
  priceLabel?: string // For custom/scoped pricing
  duration?: string
  unitLabel?: string // e.g., "per license", "per km²"
  metadata?: Record<string, string | number | boolean>
  recommended?: boolean
  included?: boolean // For items included with other purchases
  minQuantity?: number
  maxQuantity?: number
}

export interface PricingCategoryConfig {
  id: PricingCategory
  name: string
  description: string
  selectionMode: SelectionMode
  items: PricingItem[]
}

// ============================================================================
// PILOT PACKAGES
// ============================================================================
const pilotPackages: PricingCategoryConfig = {
  id: 'pilot_packages',
  name: 'Pilot Packages',
  description: 'Turnkey pilot programs for rapid deployment and validation',
  selectionMode: 'single',
  items: [
    {
      id: 'pilot-1',
      partNumber: 'PILOT-1',
      name: 'Standard Pilot',
      description: '10 licenses, 25,000 km² maps, turnkey integration (INT-KEY)',
      price: 220000,
      duration: '3 months',
      recommended: true,
      metadata: {
        licenses: 10,
        maps_km2: 25000,
        includes: 'INT-KEY',
      },
    },
    {
      id: 'pilot-2',
      partNumber: 'PILOT-2',
      name: 'Advanced Pilot',
      description: 'Standard Pilot + Theseus-led high altitude R&D (RND-6K-THE)',
      price: 445000,
      duration: '3 months',
      metadata: {
        licenses: 10,
        maps_km2: 25000,
        includes: 'INT-KEY, RND-6K-THE',
      },
    },
  ],
}

// ============================================================================
// COMPUTE-BASED SOFTWARE LICENSES
// ============================================================================
const licenses: PricingCategoryConfig = {
  id: 'licenses',
  name: 'Software Licenses',
  description: 'Compute-based licenses with included map coverage. All licenses include Standard Support.',
  selectionMode: 'single',
  items: [
    {
      id: 'lic-trial',
      partNumber: 'LIC-TRIAL',
      name: 'Trial',
      description: '2 licenses, 100 km² maps',
      price: 0,
      unitLabel: 'Free',
      metadata: {
        licenses: 2,
        maps_km2: 100,
        unit_price: 0,
      },
    },
    {
      id: 'lic-10',
      partNumber: 'LIC-10',
      name: 'Starter',
      description: '10 licenses, 25,000 km² maps',
      price: 25000,
      unitLabel: '$2,500/license',
      metadata: {
        licenses: 10,
        maps_km2: 25000,
        unit_price: 2500,
      },
    },
    {
      id: 'lic-100',
      partNumber: 'LIC-100',
      name: 'Growth',
      description: '100 licenses, 100,000 km² maps',
      price: 180000,
      unitLabel: '$1,800/license',
      recommended: true,
      metadata: {
        licenses: 100,
        maps_km2: 100000,
        unit_price: 1800,
      },
    },
    {
      id: 'lic-500',
      partNumber: 'LIC-500',
      name: 'Scale',
      description: '500 licenses, 200,000 km² maps',
      price: 500000,
      unitLabel: '$1,000/license',
      metadata: {
        licenses: 500,
        maps_km2: 200000,
        unit_price: 1000,
      },
    },
    {
      id: 'lic-1000',
      partNumber: 'LIC-1000',
      name: 'Enterprise',
      description: '1,000 licenses, 350,000 km² maps',
      price: 700000,
      unitLabel: '$700/license',
      metadata: {
        licenses: 1000,
        maps_km2: 350000,
        unit_price: 700,
      },
    },
  ],
}

// ============================================================================
// MAPS (ANNUAL ADD-ONS)
// ============================================================================
const maps: PricingCategoryConfig = {
  id: 'maps',
  name: 'Map Coverage',
  description: 'Annual map add-ons for additional coverage beyond license allocation',
  selectionMode: 'multiple',
  items: [
    {
      id: 'map-25k',
      partNumber: 'MAP-25K',
      name: '25,000 km²',
      description: '25,000 km² additional coverage',
      price: 6000,
      unitLabel: '$0.24/km²',
      metadata: {
        coverage_km2: 25000,
        price_per_km2: 0.24,
      },
    },
    {
      id: 'map-50k',
      partNumber: 'MAP-50K',
      name: '50,000 km²',
      description: '50,000 km² additional coverage',
      price: 10000,
      unitLabel: '$0.20/km²',
      metadata: {
        coverage_km2: 50000,
        price_per_km2: 0.2,
      },
    },
    {
      id: 'map-100k',
      partNumber: 'MAP-100K',
      name: '100,000 km²',
      description: '100,000 km² additional coverage',
      price: 18000,
      unitLabel: '$0.18/km²',
      recommended: true,
      metadata: {
        coverage_km2: 100000,
        price_per_km2: 0.18,
      },
    },
    {
      id: 'map-250k',
      partNumber: 'MAP-250K',
      name: '250,000 km²',
      description: '250,000 km² additional coverage',
      price: 40000,
      unitLabel: '$0.16/km²',
      metadata: {
        coverage_km2: 250000,
        price_per_km2: 0.16,
      },
    },
    {
      id: 'map-1m',
      partNumber: 'MAP-1M',
      name: '1,000,000 km²',
      description: '1,000,000 km² additional coverage',
      price: 120000,
      unitLabel: '$0.12/km²',
      metadata: {
        coverage_km2: 1000000,
        price_per_km2: 0.12,
      },
    },
    {
      id: 'map-ext',
      partNumber: 'MAP-EXT',
      name: 'Custom Provider',
      description: 'Custom provider integration - scoped per project',
      price: 0,
      priceLabel: 'Scoped',
      metadata: {
        custom: true,
      },
    },
  ],
}

// ============================================================================
// ANNUAL SUPPORT TIERS
// ============================================================================
const support: PricingCategoryConfig = {
  id: 'support',
  name: 'Support Tiers',
  description: 'Annual support packages with varying SLA levels',
  selectionMode: 'single',
  items: [
    {
      id: 'sup-std',
      partNumber: 'SUP-STD',
      name: 'Standard Support',
      description: 'Documentation, email support. 5 business day response SLA.',
      price: 0,
      unitLabel: 'Included',
      included: true,
      metadata: {
        response_sla: '5 business days',
      },
    },
    {
      id: 'sup-prem',
      partNumber: 'SUP-PREM',
      name: 'Premium Support',
      description: 'Standard + remote debugging, direct Slack/Signal access. 48 hour response SLA.',
      price: 30000,
      recommended: true,
      metadata: {
        response_sla: '48 hours',
      },
    },
    {
      id: 'sup-ent',
      partNumber: 'SUP-ENT',
      name: 'Enterprise Support',
      description: 'Premium + dedicated engineer, regular reports, roadmap input. 4 hour response SLA.',
      price: 75000,
      metadata: {
        response_sla: '4 hours',
      },
    },
  ],
}

// ============================================================================
// INTEGRATION SERVICES
// ============================================================================
const integration: PricingCategoryConfig = {
  id: 'integration',
  name: 'Integration Services',
  description: 'Camera integration options. Cyclops works out of the box with FLIR Boson Plus.',
  selectionMode: 'single',
  items: [
    {
      id: 'int-self',
      partNumber: 'INT-SELF',
      name: 'Self-Service',
      description: 'Self-service integration with documentation. Customer leads hardware selection and integration.',
      price: 0,
      unitLabel: 'Included',
      included: true,
      duration: 'Variable',
    },
    {
      id: 'int-sup',
      partNumber: 'INT-SUP',
      name: 'Supported Integration',
      description: 'Engineering support for camera selection and integration decisions. Customer-led with Theseus guidance.',
      price: 40000,
      duration: 'Variable',
    },
    {
      id: 'int-key',
      partNumber: 'INT-KEY',
      name: 'Turnkey Integration',
      description: 'Full turnkey integration. Theseus handles camera selection, develops integration, and delivers complete solution.',
      price: 195000,
      duration: '3 months',
      recommended: true,
    },
  ],
}

// ============================================================================
// R&D SERVICES
// ============================================================================
const rnd: PricingCategoryConfig = {
  id: 'rnd',
  name: 'R&D Services',
  description: 'Custom development and altitude certification services',
  selectionMode: 'multiple',
  items: [
    {
      id: 'rnd-2k-sup',
      partNumber: 'RND-2K-SUP',
      name: '2km Altitude (Customer-Led)',
      description: '10 hrs flight testing, algorithm modification, validation report. Customer leads testing.',
      price: 35000,
      metadata: {
        altitude_ceiling: '2 km',
        flight_hours: 10,
        testing_lead: 'Customer',
      },
    },
    {
      id: 'rnd-2k-the',
      partNumber: 'RND-2K-THE',
      name: '2km Altitude (Theseus-Led)',
      description: '10 hrs flight testing, algorithm modification, validation report, Ukraine validation. Theseus leads testing.',
      price: 115000,
      metadata: {
        altitude_ceiling: '2 km',
        flight_hours: 10,
        testing_lead: 'Theseus',
        ukraine_validation: true,
      },
    },
    {
      id: 'rnd-6k-sup',
      partNumber: 'RND-6K-SUP',
      name: '6km Altitude (Customer-Led)',
      description: '20 hrs flight testing, core algorithm development, validation report. Customer leads testing.',
      price: 140000,
      metadata: {
        altitude_ceiling: '6 km',
        flight_hours: 20,
        testing_lead: 'Customer',
      },
    },
    {
      id: 'rnd-6k-the',
      partNumber: 'RND-6K-THE',
      name: '6km Altitude (Theseus-Led)',
      description: '20 hrs flight testing, core algorithm development, validation report, Ukraine validation. Theseus leads testing.',
      price: 225000,
      recommended: true,
      metadata: {
        altitude_ceiling: '6 km',
        flight_hours: 20,
        testing_lead: 'Theseus',
        ukraine_validation: true,
      },
    },
    {
      id: 'rnd-tune',
      partNumber: 'RND-TUNE',
      name: 'Custom Tuning',
      description: 'Custom tuning for specific requirements - scoped per project',
      price: 0,
      priceLabel: 'Scoped',
      metadata: {
        custom: true,
      },
    },
  ],
}

// ============================================================================
// FIELD SERVICES
// ============================================================================
const fieldServices: PricingCategoryConfig = {
  id: 'field_services',
  name: 'Field Services',
  description: 'On-site engineering support and testing services',
  selectionMode: 'quantity',
  items: [
    {
      id: 'fsr-1d',
      partNumber: 'FSR-1D',
      name: 'Field Service Rep (1 Day)',
      description: 'On-site engineering support for 1 day',
      price: 1700,
      duration: '1 day',
      minQuantity: 1,
      maxQuantity: 30,
    },
    {
      id: 'fsr-1w',
      partNumber: 'FSR-1W',
      name: 'Field Service Rep (1 Week)',
      description: 'On-site engineering support for 5 days',
      price: 8500,
      duration: '5 days',
      minQuantity: 1,
      maxQuantity: 12,
    },
    {
      id: 'fsr-test',
      partNumber: 'FSR-TEST',
      name: 'Testing Support',
      description: 'Test plan, execution support, executive report with integration validation, GPS-denied capability demonstration',
      price: 17000,
      duration: '2 weeks',
      minQuantity: 1,
      maxQuantity: 4,
    },
  ],
}

// ============================================================================
// COMPLETE PRICING CATALOG
// ============================================================================
export const pricingCatalog: PricingCategoryConfig[] = [
  pilotPackages,
  licenses,
  maps,
  support,
  integration,
  rnd,
  fieldServices,
]

// Helper to get category by ID
export function getCategoryById(id: PricingCategory): PricingCategoryConfig | undefined {
  return pricingCatalog.find((cat) => cat.id === id)
}

// Helper to get item by ID across all categories
export function getItemById(itemId: string): PricingItem | undefined {
  for (const category of pricingCatalog) {
    const item = category.items.find((i) => i.id === itemId)
    if (item) return item
  }
  return undefined
}

// Format price for display
export function formatPrice(price: number, priceLabel?: string): string {
  if (priceLabel) return priceLabel
  if (price === 0) return 'Included'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price)
}

// Quote item with quantity tracking
export interface QuoteItem {
  item: PricingItem
  categoryId: PricingCategory
  quantity: number
  customPrice?: number // For scoped items
}

// Quote state
export interface Quote {
  customerName: string
  customerEmail?: string
  customerCompany?: string
  items: QuoteItem[]
  discount: number // Percentage (0-100)
  notes: string
  validityDays: number
  createdAt: Date
}

// Calculate quote totals
export function calculateQuoteTotals(quote: Quote) {
  let subtotal = 0
  const categoryTotals: Record<PricingCategory, number> = {
    pilot_packages: 0,
    licenses: 0,
    maps: 0,
    support: 0,
    integration: 0,
    rnd: 0,
    field_services: 0,
  }

  for (const quoteItem of quote.items) {
    const itemTotal = (quoteItem.customPrice ?? quoteItem.item.price) * quoteItem.quantity
    subtotal += itemTotal
    categoryTotals[quoteItem.categoryId] += itemTotal
  }

  const discountAmount = subtotal * (quote.discount / 100)
  const total = subtotal - discountAmount

  return {
    subtotal,
    categoryTotals,
    discountAmount,
    total,
  }
}

// Create empty quote
export function createEmptyQuote(): Quote {
  return {
    customerName: '',
    customerEmail: '',
    customerCompany: '',
    items: [],
    discount: 0,
    notes: '',
    validityDays: 30,
    createdAt: new Date(),
  }
}

